import { useCallback, useMemo } from 'react';
import { PageWithTitle } from 'src/Common';
import {
  Box,
  Breadcrumbs,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { CreateCryptoForm, ExchangeMapper } from '../components';

enum EditTabs {
  mapper = 0,
  create = 1
}

type TabOptions = keyof typeof EditTabs;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ paddingY: 3 }}>{children}</Box>}
    </div>
  );
}

export const CryptoEditorPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchParamTab: TabOptions | undefined = useMemo(() => {
    const tab = searchParams.get('tab');

    if (tab && Object.keys(EditTabs).includes(tab)) {
      return tab as TabOptions;
    } else {
      return 'mapper';
    }
  }, [searchParams]);

  const activeTab = useMemo(() => EditTabs[searchParamTab], [searchParamTab]);

  const setActiveTab = useCallback(
    (activeTab: number) => {
      setSearchParams({
        ...Object.fromEntries(searchParams.entries()),
        tab: EditTabs[activeTab] as string
      });
    },
    [setSearchParams, searchParams]
  );

  function tabProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`
    };
  }

  const handleChangeTab = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <PageWithTitle
      title={
        <Stack spacing={2}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component={RouterLink}
              underline="hover"
              key="1"
              color="inherit"
              to="/arbitrage"
            >
              Config Editor
            </Link>
            <Typography key="2" color="text.primary">
              Crypto Management
            </Typography>
          </Breadcrumbs>
        </Stack>
      }
      style={{ padding: '2rem 5%' }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          aria-label="crypto editor tabs"
        >
          <Tab label="Exchange Mapper" {...tabProps(EditTabs.mapper)} />
          <Tab label="Create Crypto" {...tabProps(EditTabs.create)} />
        </Tabs>
      </Box>
      <TabPanel value={activeTab} index={EditTabs.mapper}>
        <ExchangeMapper />
      </TabPanel>
      <TabPanel value={activeTab} index={EditTabs.create}>
        <CreateCryptoForm />
      </TabPanel>
    </PageWithTitle>
  );
};
